import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AlertTriangle, CheckCircle2, Lightbulb, ThumbsDown, ThumbsUp } from 'lucide-react';
import { InsightItem, SuperficieInsight, TipoInsight } from '../../services/insights';
import { useInsights } from './InsightsProvider';

const CORES: Record<TipoInsight, { fundo: string; texto: string; rotulo: string }> = {
  aviso: { fundo: '#fef3c7', texto: '#92400e', rotulo: 'Aviso' },
  acao: { fundo: '#dbeafe', texto: '#1e40af', rotulo: 'Ação' },
  dica: { fundo: '#d1fae5', texto: '#065f46', rotulo: 'Dica' },
};

const ICONES: Record<TipoInsight, React.ReactNode> = {
  aviso: <AlertTriangle size={12} />,
  acao: <CheckCircle2 size={12} />,
  dica: <Lightbulb size={12} />,
};

const Cartao = styled.article<{ $urgente: boolean }>`
  background: #fff;
  border: 1px solid ${({ $urgente }) => ($urgente ? '#fcd34d' : '#e2e8f0')};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Topo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Chip = styled.span<{ $fundo: string; $texto: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $texto }) => $texto};
`;

const Novo = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1f467e;
`;

const Titulo = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: #1f2937;
`;

const Texto = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: #475569;
`;

const Retorno = styled.div`
  font-size: 12px;
  color: #1f467e;
  background: #e8eef7;
  border-radius: 8px;
  padding: 8px 10px;
`;

const Acoes = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  flex-wrap: wrap;
`;

const BotaoPrincipal = styled.button`
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: #1f467e;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #17355f; }
`;

const BotaoSecundario = styled.button`
  height: 32px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const BotaoIcone = styled.button<{ $ativo: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid ${({ $ativo }) => ($ativo ? '#1f467e' : '#e2e8f0')};
  background: ${({ $ativo }) => ($ativo ? '#e8eef7' : '#fff')};
  color: ${({ $ativo }) => ($ativo ? '#1f467e' : '#64748b')};
`;

const LinkPorQue = styled.button`
  align-self: flex-start;
  background: none;
  border: 0;
  padding: 0;
  font-size: 11px;
  color: #64748b;
  text-decoration: underline;
  cursor: pointer;
`;

const PorQue = styled.div`
  font-size: 12px;
  color: #475569;
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px 10px;
`;

interface Props {
  item: InsightItem;
  superficie: SuperficieInsight;
  posicao: number;
  /** No card do dashboard: sem feedback nem "por que", para caber em três colunas. */
  compacto?: boolean;
  aoNavegar?: () => void;
}

/** Um insight com as ações dele. Conta a exibição quando ao menos 60% do card fica visível. */
export const InsightCard: React.FC<Props> = ({ item, superficie, posicao, compacto, aoNavegar }) => {
  const { registrarExibicao, interagir } = useInsights();
  const history = useHistory();
  const ref = useRef<HTMLElement>(null);
  const [retorno, setRetorno] = useState<'util' | 'nao_util' | null>(null);
  const [mostrarPorQue, setMostrarPorQue] = useState(false);
  const cor = CORES[item.tipo] ?? CORES.dica;

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento || typeof IntersectionObserver === 'undefined') {
      registrarExibicao(item, superficie);
      return undefined;
    }
    const observador = new IntersectionObserver(entradas => {
      if (entradas.some(e => e.isIntersecting)) {
        registrarExibicao(item, superficie);
        observador.disconnect();
      }
    }, { threshold: 0.6 });
    observador.observe(elemento);
    return () => observador.disconnect();
  }, [item, superficie, registrarExibicao]);

  const abrirAcao = () => {
    interagir(item, 'cta', superficie, posicao);
    if (item.linkCta) history.push(item.linkCta);
    aoNavegar?.();
  };

  const avaliar = (acao: 'util' | 'nao_util') => {
    if (retorno) return;
    setRetorno(acao);
    if (acao === 'util') {
      interagir(item, 'util', superficie, posicao);
    } else {
      // Deixa a resposta visível um instante antes de o card sumir.
      window.setTimeout(() => interagir(item, 'nao_util', superficie, posicao), 1400);
    }
  };

  const alternarPorQue = () => {
    if (!mostrarPorQue) interagir(item, 'por_que', superficie, posicao);
    setMostrarPorQue(v => !v);
  };

  return (
    <Cartao ref={ref} $urgente={item.urgente}>
      <Topo>
        <Chip $fundo={cor.fundo} $texto={cor.texto}>{ICONES[item.tipo]}{cor.rotulo}</Chip>
        {item.novo && <Novo>Novo</Novo>}
      </Topo>
      <Titulo>{item.titulo}</Titulo>
      {item.texto && <Texto>{item.texto}</Texto>}
      {retorno === 'util' && <Retorno>Anotado! Vou trazer mais insights assim.</Retorno>}
      {retorno === 'nao_util' && <Retorno>Entendido, não mostro mais este.</Retorno>}
      <Acoes>
        {item.linkCta && <BotaoPrincipal type="button" onClick={abrirAcao}>{item.textoCta || 'Ver'}</BotaoPrincipal>}
        <BotaoSecundario type="button" onClick={() => interagir(item, 'depois', superficie, posicao)}>Depois</BotaoSecundario>
        {!compacto && (
          <>
            <span style={{ flex: 1 }} />
            <BotaoIcone type="button" aria-label="Útil" title="Útil" $ativo={retorno === 'util'} onClick={() => avaliar('util')}>
              <ThumbsUp size={15} />
            </BotaoIcone>
            <BotaoIcone type="button" aria-label="Não é útil" title="Não é útil" $ativo={retorno === 'nao_util'} onClick={() => avaliar('nao_util')}>
              <ThumbsDown size={15} />
            </BotaoIcone>
          </>
        )}
      </Acoes>
      {!compacto && item.porQue && (
        <>
          <LinkPorQue type="button" onClick={alternarPorQue}>Por que estou vendo isto?</LinkPorQue>
          {mostrarPorQue && <PorQue>{item.porQue}</PorQue>}
        </>
      )}
    </Cartao>
  );
};
